import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Building2, Phone, MapPin, Mail, Radio, Video, CreditCard,
  LifeBuoy, HardDrive, Calendar, ShieldCheck, Clock, CheckCircle2, Film, LogIn,
  Activity, UserPlus, UploadCloud, Receipt,
} from "lucide-react";
import api from "@/lib/api";
import { InvoiceHistory } from "@/components/InvoiceHistory";
import { toast } from "sonner";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso || "—"; } };
const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso || "—"; } };
const fmtSize = (mb) => { const n = Number(mb) || 0; return n >= 1024 ? `${(n / 1024).toFixed(1)} GB` : `${n.toFixed(0)} MB`; };
const fmtDur = (s) => { if (!s && s !== 0) return "—"; const m = Math.floor(s / 60), sec = Math.floor(s % 60); return `${m}:${String(sec).padStart(2, "0")}`; };

const STATUS = {
  pending: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  approved: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  rejected: "bg-red-500/15 text-red-400 border-red-500/30",
};

const ACTIVITY = {
  account: { icon: UserPlus, cls: "text-sky-400 bg-sky-500/10 border-sky-500/25" },
  activated: { icon: CheckCircle2, cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25" },
  plan: { icon: CreditCard, cls: "text-amber-400 bg-amber-500/10 border-amber-500/25" },
  golive: { icon: Radio, cls: "text-red-400 bg-red-500/10 border-red-500/25" },
  upload: { icon: UploadCloud, cls: "text-indigo-400 bg-indigo-500/10 border-indigo-500/25" },
  ticket: { icon: LifeBuoy, cls: "text-gray-300 bg-gray-700/40 border-gray-700" },
};

const StreamStatus = ({ s }) => {
  const live = s === "active" || s === "live";
  return <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border " + (live ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-gray-700/40 text-gray-400 border-gray-700")}>
    <span className={"h-1.5 w-1.5 rounded-full " + (live ? "bg-emerald-400 animate-live-dot" : "bg-gray-500")} />{live ? "Live" : "Offline"}
  </span>;
};

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-800 bg-[#0E131F] p-4">
      <div className="flex items-center gap-2 text-gray-500 text-xs font-medium"><Icon className="h-4 w-4 text-amber-400" />{label}</div>
      <p className="mt-1.5 text-2xl font-bold text-white font-display">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ icon: Icon, title, count, children }) {
  return (
    <div className="rounded-2xl border border-gray-800 bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-800 flex items-center gap-2">
        <Icon className="h-4 w-4 text-amber-400" />
        <h2 className="font-display font-semibold text-sm">{title}</h2>
        {count != null && <span className="text-xs text-gray-500">({count})</span>}
      </div>
      {children}
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/users/${id}/detail`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Couldn't load client"))
      .finally(() => setLoading(false));
  }, [id]);

  const accessAccount = async () => {
    try {
      await api.post(`/admin/users/${id}/impersonate`);
      toast.success("Accessing account…");
      window.location.href = "/streams";
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't access this account"); }
  };

  if (loading) return <div className="flex items-center justify-center py-40"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;
  if (!data) return <div className="p-8 text-gray-400">Client not found. <Link to="/team" className="text-amber-400">Back to Clients</Link></div>;

  const { profile: p, streams, media, media_total_mb, billing: b, tickets, activity = [] } = data;
  const liveCount = streams.filter((s) => s.status === "active" || s.status === "live").length;
  const st = STATUS[p.status] || STATUS.approved;

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="client-detail-page">
      <button data-testid="back-to-clients" onClick={() => navigate("/team")} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-5 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Clients
      </button>

      {/* Header */}
      <div className="rounded-2xl border border-gray-800 bg-card p-6 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            {p.picture ? <img src={p.picture} alt="" className="h-14 w-14 rounded-full object-cover" /> : <div className="h-14 w-14 rounded-full gold-gradient flex items-center justify-center text-xl font-bold text-black">{(p.name || "U")[0]}</div>}
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2.5">
                {p.name || "—"}
                {p.role === "admin"
                  ? <span className="inline-flex items-center gap-1 text-amber-400 text-xs font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span>
                  : <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border capitalize " + st}>{p.status}</span>}
              </h1>
              <div className="mt-1.5 flex items-center gap-4 flex-wrap text-sm text-gray-400">
                <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-500" />{p.organization || "—"}</span>
                <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1.5 text-amber-400 hover:underline"><Mail className="h-3.5 w-3.5" />{p.email}</a>
                {p.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-gray-500" />{p.phone}</span>}
              </div>
              {(p.address || p.city || p.state || p.zip) && (
                <p className="mt-1 text-sm text-gray-500 inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />{[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")}
                </p>
              )}
            </div>
          </div>
          {p.role !== "admin" && (
            <button data-testid="client-access-btn" onClick={accessAccount} className="inline-flex items-center gap-2 px-4 h-10 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 text-sm font-medium transition-colors">
              <LogIn className="h-4 w-4" /> Access Account
            </button>
          )}
        </div>
        <div className="mt-5 flex items-center gap-5 flex-wrap text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Joined {fmtDate(p.created_at)}</span>
          {p.date_activated && <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Activated {fmtDate(p.date_activated)}</span>}
          {p.heard_from && <span>Heard via <span className="text-gray-300">{p.heard_from}</span></span>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat icon={Radio} label="Streams" value={streams.length} sub={`${liveCount} live now`} />
        <Stat icon={Film} label="Recordings & Videos" value={media.length} />
        <Stat icon={HardDrive} label="Storage Used" value={fmtSize(media_total_mb)} />
        <Stat icon={LifeBuoy} label="Support Tickets" value={tickets.length} sub={`${tickets.filter((t) => !t.read).length} unread`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Streams */}
          <Section icon={Radio} title="Streams" count={streams.length}>
            {streams.length === 0 ? <p className="px-5 py-8 text-sm text-gray-500 text-center">No streams.</p> : (
              <div className="divide-y divide-gray-800/60">
                {streams.map((s) => (
                  <div key={s.id} data-testid={`detail-stream-${s.id}`} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-gray-800/20">
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{s.title}</p>
                      <p className="text-xs text-gray-500 truncate">{s.server_location || s.stream_type || ""}</p>
                    </div>
                    <StreamStatus s={s.status} />
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Recordings & Videos */}
          <Section icon={Video} title="Recordings & Videos" count={media.length}>
            {media.length === 0 ? <p className="px-5 py-8 text-sm text-gray-500 text-center">No recordings or uploads.</p> : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 p-5">
                {media.slice(0, 18).map((m) => (
                  <div key={`${m.kind}-${m.id}`} data-testid={`detail-media-${m.id}`} className="rounded-xl border border-gray-800 bg-[#0E131F] overflow-hidden">
                    <div className="aspect-video bg-black/50 relative">
                      {m.thumbnail_url ? <img src={m.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Film className="h-6 w-6 text-gray-700" /></div>}
                      <span className="absolute top-1.5 left-1.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/70 text-gray-200 capitalize">{m.kind}</span>
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs text-white font-medium truncate" title={m.title}>{m.title || "Untitled"}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5">{fmtSize(m.size_mb)} · {fmtDur(m.duration_seconds)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Activity timeline */}
          <Section icon={Activity} title="Recent Activity" count={activity.length}>
            {activity.length === 0 ? <p className="px-5 py-8 text-sm text-gray-500 text-center">No activity yet.</p> : (
              <div className="p-5">
                <ol className="relative border-l border-gray-800 ml-2 space-y-5">
                  {activity.map((a, i) => {
                    const cfg = ACTIVITY[a.type] || ACTIVITY.ticket;
                    const Icon = cfg.icon;
                    return (
                      <li key={i} data-testid={`activity-${i}`} className="ml-5">
                        <span className={"absolute -left-[13px] flex h-6 w-6 items-center justify-center rounded-full border " + cfg.cls}>
                          <Icon className="h-3 w-3" />
                        </span>
                        <div className="flex items-baseline justify-between gap-3">
                          <p className="text-sm text-white font-medium">{a.title}{a.detail ? <span className="text-gray-400 font-normal"> — {a.detail}</span> : null}</p>
                          <span className="shrink-0 text-[11px] text-gray-600">{fmtDateTime(a.at)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </Section>
        </div>

        <div className="space-y-6">
          {/* Billing */}
          <Section icon={CreditCard} title="Billing">
            <div className="p-5 space-y-3 text-sm">
              <div className="flex items-center justify-between"><span className="text-gray-500">Plan</span><span className="text-white font-semibold">{b.plan_name || "Free Trial"}</span></div>
              {b.has_subscription ? (
                <>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Subscription</span><span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span></div>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Sub ID</span><span className="text-gray-300 text-xs font-mono">{b.subscription_id}</span></div>
                  {b.plan_started_at && <div className="flex items-center justify-between"><span className="text-gray-500">Started</span><span className="text-gray-300">{fmtDate(b.plan_started_at)}</span></div>}
                </>
              ) : b.pending_payment_plan ? (
                <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 text-xs text-emerald-300">Payment claimed — awaiting activation of {b.pending_payment_plan}</div>
              ) : (
                <>
                  <div className="flex items-center justify-between"><span className="text-gray-500">Subscription</span><span className="text-gray-400 text-xs">No active subscription</span></div>
                  {b.trial_ends_at && <div className="flex items-center justify-between"><span className="text-gray-500">Trial ends</span><span className="text-gray-300">{fmtDate(b.trial_ends_at)}</span></div>}
                </>
              )}
            </div>
          </Section>

          <Section icon={Receipt} title="Invoices">
            <InvoiceHistory userId={id} />
          </Section>

          {/* Support tickets */}
          <Section icon={LifeBuoy} title="Support Tickets" count={tickets.length}>
            {tickets.length === 0 ? <p className="px-5 py-8 text-sm text-gray-500 text-center">No tickets submitted.</p> : (
              <div className="divide-y divide-gray-800/60">
                {tickets.map((t) => (
                  <div key={t.id} data-testid={`detail-ticket-${t.id}`} className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      {!t.read && <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />}
                      <p className="text-sm text-white font-medium truncate">{t.subject}</p>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 line-clamp-2 whitespace-pre-wrap">{t.message}</p>
                    <p className="mt-1 text-[11px] text-gray-600 inline-flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDateTime(t.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}
